import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/ContactUs.css';
import ScrollProgressIndicator from '../components/ScrollProgressIndicator';
import SEOHead from '../components/SEOHead';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    email: '',
    services: [],
    customQuery: '',
    budget: '',
    timeline: '',
    company: ''
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
      
      // Set required attributes for iOS
      const video = videoRef.current;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.autoplay = true;
      
      // Force play on iOS
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Contact video autoplay started');
          })
          .catch((error) => {
            console.log('Contact video autoplay prevented:', error);
          });
      }
    }

    // Cleanup any event listeners if needed in the future
    return () => {
      // Currently no cleanup needed
    };
  }, []);

  const services = [
    { name: "Website Design", icon: "🎨", color: "#3498db" },
    { name: "SEO Optimization", icon: "🚀", color: "#e74c3c" },
    { name: "Social Media Management", icon: "📱", color: "#9b59b6" },
    { name: "Content Creation", icon: "✍️", color: "#f39c12" },
    { name: "Video Production", icon: "🎬", color: "#27ae60" },
    { name: "Facebook Marketing", icon: "📊", color: "#34495e" }
  ];

  const budgetRanges = [
    "₹10,000 - ₹25,000",
    "₹25,000 - ₹50,000",
    "₹50,000 - ₹1,00,000",
    "₹1,00,000+"
  ];

  const timelines = [
    "1-2 weeks",
    "2-4 weeks",
    "1-2 months",
    "2-3 months",
    "3+ months"
  ];

  const contactMethods = [
    {
      icon: "📧",
      title: "Email Us",
      description: "Drop us a line anytime",
      contact: "info@quantumframestudio.com",
      color: "#3498db"
    },
    {
      icon: "📱",
      title: "Call Us",
      description: "Speak with our team",
      contact: "+91 9243015433",
      color: "#27ae60"
    },
    {
      icon: "📍",
      title: "Visit Us",
      description: "Come to our office",
      contact: "Bhopal, Madhya Pradesh",
      color: "#e74c3c"
    },
    {
      icon: "💬",
      title: "WhatsApp",
      description: "Quick chat on WhatsApp",
      contact: "+91 9243015433",
      color: "#25d366"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to build a website?",
      answer: "Typically 2-6 weeks depending on complexity and requirements. We'll provide a detailed timeline after understanding your needs."
    },
    {
      question: "Do you provide ongoing support?",
      answer: "Yes! We offer maintenance packages and ongoing support to keep your website running smoothly and up-to-date."
    },
    {
      question: "What's included in your SEO services?",
      answer: "Our SEO services include keyword research, on-page optimization, content strategy, technical SEO, and monthly performance reports."
    },
    {
      question: "Can you work with my existing brand?",
      answer: "Absolutely! We work with your existing brand guidelines or help you develop a new brand identity that resonates with your audience."
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    validateField(name, value);
  };

  const handleServiceChange = (service) => {
    setFormData(prevState => ({
      ...prevState,
      services: prevState.services.includes(service)
        ? prevState.services.filter(s => s !== service)
        : [...prevState.services, service]
    }));
  };

  const validateField = (name, value) => {
    let error = null;
    switch (name) {
      case 'name':
        if (!value.trim()) error = "Name is required";
        break;
      case 'contactNumber':
        if (!value.trim()) error = "Contact number is required";
        else if (!/^(\+\d{1,3}[- ]?)?\d{10}$/.test(value)) error = "Invalid phone number";
        break;
      case 'email':
        if (!value.trim()) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Invalid email address";
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const validateForm = () => {
    const fields = ['name', 'contactNumber', 'email'];
    const formIsValid = fields.every(field => validateField(field, formData[field]));
    if (formData.services.length === 0) {
      setErrors(prev => ({ ...prev, services: "Please select at least one service" }));
      return false;
    }
    return formIsValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const message = `*New Contact Inquiry*%0A%0A*Name:* ${formData.name}%0A*Company:* ${formData.company || 'Not specified'}%0A*Contact Number:* ${formData.contactNumber}%0A*Email:* ${formData.email}%0A*Services Required:* ${formData.services.join(', ')}%0A*Budget Range:* ${formData.budget || 'Not specified'}%0A*Timeline:* ${formData.timeline || 'Not specified'}%0A*Query:* ${formData.customQuery}`;
      
      const whatsappUrl = `https://wa.me/919243015433?text=${message}`;
      window.open(whatsappUrl, '_blank');
      
      setFormData({
        name: '',
        contactNumber: '',
        email: '',
        services: [],
        customQuery: '',
        budget: '',
        timeline: '',
        company: ''
      });
      setSubmitStatus('success');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="ct-contact-form-container">
      <SEOHead page="contact" />
      <ScrollProgressIndicator />
      
      {/* Floating Particles */}
      <div className="ct-particles-container">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="ct-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        className="ct-background-video"
        playsInline
        webkit-playsinline="true"
        preload="auto"
        style={{ pointerEvents: 'none' }}
      >
        <source src="/assets/video/contact-us-vid.mp4" type="video/mp4" />
      </video>
      
      {/* Hero Section */}
      <motion.section
        className="ct-hero-section"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <motion.div className="ct-hero-content">
          <motion.h1 
            className="ct-hero-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Let's Create Something <span className="ct-highlight">Amazing</span> Together
          </motion.h1>
          <motion.p 
            className="ct-hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Ready to transform your digital presence? We're here to help bring your vision to life with our expertise in web development and digital marketing.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* Contact Methods Section */}
      <motion.section 
        className="ct-contact-methods"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="ct-container">
          <motion.h2 className="ct-section-title" variants={itemVariants}>
            Get In Touch
          </motion.h2>
          <div className="ct-contact-grid">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                className="ct-contact-method"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05, 
                  rotateY: 5,
                  boxShadow: `0 20px 40px rgba(0, 0, 0, 0.2)`
                }}
                style={{ '--method-color': method.color }}
              >
                <div className="ct-method-icon">{method.icon}</div>
                <h3 className="ct-method-title">{method.title}</h3>
                <p className="ct-method-description">{method.description}</p>
                <div className="ct-method-contact">{method.contact}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Main Contact Form */}
      <motion.div 
        className="ct-enhanced-contact-form"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2
          variants={itemVariants}
          className="ct-form-title"
        >
          Start Your Project Today
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="ct-form-subtitle"
        >
          Fill out this form, and we'll send you a personalized quotation for our services!
        </motion.p>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <motion.div className="ct-form-section" variants={itemVariants}>
            <h3 className="ct-section-heading">Basic Information</h3>
            <div className="ct-form-row">
              {['name', 'company'].map((field, index) => (
                <motion.div 
                  key={field}
                  className="ct-form-group"
                  variants={itemVariants}
                >
                  <label htmlFor={`ecf-${field}`}>
                    {field === 'name' ? 'Full Name' : 'Company/Organization'} {field === 'name' && '*'}:
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.3)" }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    type="text"
                    id={`ecf-${field}`}
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className={errors[field] ? 'error' : (formData[field] && !errors[field] ? 'valid' : '')}
                    required={field === 'name'}
                    placeholder={field === 'name' ? 'Enter your full name' : 'Enter company name (optional)'}
                  />
                  <AnimatePresence mode="wait">
                    {errors[field] && (
                      <motion.span
                        className="ct-error-message"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {errors[field]}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
            
            <div className="ct-form-row">
              {['contactNumber', 'email'].map((field, index) => (
                <motion.div 
                  key={field}
                  className="ct-form-group"
                  variants={itemVariants}
                >
                  <label htmlFor={`ecf-${field}`}>
                    {field === 'contactNumber' ? 'Phone Number*' : 'Email Address*'}:
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.3)" }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    type={field === 'email' ? 'email' : 'tel'}
                    id={`ecf-${field}`}
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className={errors[field] ? 'error' : (formData[field] && !errors[field] ? 'valid' : '')}
                    required
                    placeholder={field === 'contactNumber' ? '+91 12345 67890' : 'your.email@example.com'}
                  />
                  <AnimatePresence mode="wait">
                    {errors[field] && (
                      <motion.span
                        className="ct-error-message"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {errors[field]}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Services Section */}
          <motion.div 
            className="ct-services-section"
            variants={itemVariants}
          >
            <h3 className="ct-section-heading">Services Required*</h3>
            <div className="ct-services-container">
              {services.map((service, index) => (
                <motion.div 
                  key={service.name}
                  className={`ct-service-item ${formData.services.includes(service.name) ? 'ct-selected' : ''}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  style={{ '--service-color': service.color }}
                  onClick={() => handleServiceChange(service.name)}
                >
                  <div className="ct-service-icon">{service.icon}</div>
                  <input
                    type="checkbox"
                    id={`ecf-${service.name}`}
                    checked={formData.services.includes(service.name)}
                    onChange={() => handleServiceChange(service.name)}
                  />
                  <label htmlFor={`ecf-${service.name}`}>{service.name}</label>
                </motion.div>
              ))}
            </div>
            <AnimatePresence mode="wait">
              {errors.services && (
                <motion.span
                  className="ct-error-message"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {errors.services}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Project Details */}
          <motion.div className="ct-form-section" variants={itemVariants}>
            <h3 className="ct-section-heading">Project Details</h3>
            <div className="ct-form-row">
              <motion.div className="ct-form-group" variants={itemVariants}>
                <label htmlFor="ecf-budget">Budget Range:</label>
                <motion.select
                  whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.3)" }}
                  id="ecf-budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                >
                  <option value="">Select budget range</option>
                  {budgetRanges.map((range, index) => (
                    <option key={index} value={range}>{range}</option>
                  ))}
                </motion.select>
              </motion.div>

              <motion.div className="ct-form-group" variants={itemVariants}>
                <label htmlFor="ecf-timeline">Project Timeline:</label>
                <motion.select
                  whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.3)" }}
                  id="ecf-timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                >
                  <option value="">Select timeline</option>
                  {timelines.map((timeline, index) => (
                    <option key={index} value={timeline}>{timeline}</option>
                  ))}
                </motion.select>
              </motion.div>
            </div>
          </motion.div>

          {/* Custom Query */}
          <motion.div 
            className="ct-form-group"
            variants={itemVariants}
          >
            <label htmlFor="ecf-customQuery">Project Description*:</label>
            <motion.textarea
              whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(52, 152, 219, 0.3)" }}
              transition={{ duration: 0.2 }}
              id="ecf-customQuery"
              name="customQuery"
              value={formData.customQuery}
              onChange={handleChange}
              rows={4}
              placeholder="Tell us more about your project, specific requirements, goals, and any other details that would help us understand your needs better..."
              required
            />
          </motion.div>

          <motion.button 
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
            className="ct-submit-button"
          >
            <span>Send Message on WhatsApp</span>
            <div className="ct-button-glow"></div>
          </motion.button>
        </form>

        <AnimatePresence mode="wait">
          {submitStatus && (
            <motion.div
              className={`ct-submit-status ${submitStatus}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {submitStatus === 'success' ? (
                <div className="ct-success-animation">
                  <svg className="ct-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <motion.circle 
                      className="ct-checkmark__circle" 
                      cx="26" 
                      cy="26" 
                      r="25" 
                      fill="none" 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    />
                    <motion.path 
                      className="ct-checkmark__check" 
                      fill="none" 
                      d="M14.1 27.2l7.1 7.2 16.7-16.8"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                    />
                  </svg>
                  <p>Thank you! Your request has been sent successfully. We'll send your personalized quotation shortly.</p>
                </div>
              ) : (
                <div className="ct-error-animation">
                  <svg className="ct-error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <motion.circle 
                      className="ct-error__circle" 
                      cx="26" 
                      cy="26" 
                      r="25" 
                      fill="none" 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <motion.path 
                      className="ct-error__cross" 
                      d="M16 16 36 36 M36 16 16 36" 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    />
                  </svg>
                  <p>There was an error submitting your form. Please try again or contact us directly.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* FAQ Section */}
      <motion.section 
        className="ct-faq-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="ct-container">
          <motion.h2 className="ct-section-title" variants={itemVariants}>
            Frequently Asked Questions
          </motion.h2>
          <div className="ct-faq-grid">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="ct-faq-item"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="ct-faq-question">{faq.question}</h3>
                <p className="ct-faq-answer">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Bottom CTA */}
      <motion.section 
        className="ct-bottom-cta"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="ct-container">
          <motion.h2 className="ct-cta-title">
            Ready to Start Your Digital Journey?
          </motion.h2>
          <motion.p className="ct-cta-description">
            Don't let your competitors get ahead. Contact us today and let's discuss how we can help transform your business with cutting-edge digital solutions.
          </motion.p>
          <div className="ct-cta-buttons">
            <motion.a
              href="tel:+919243015433"
              className="ct-cta-button primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Call Now
            </motion.a>
            <motion.a
              href="https://wa.me/919243015433"
              className="ct-cta-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              WhatsApp Us
            </motion.a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default ContactUs;